import React, { useState, useEffect } from 'react';
import { Users, UserCheck, Shield, Settings, Plus, Edit2, Trash2, Search, Eye, EyeOff } from 'lucide-react';
import {
  getAllAdminUsers,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
  toggleAdminUserStatus,
  searchAdminUsers,
  getAdminUsersStats
} from '../services/usuariosAdministrativosService';
import '../styles/usuarios-administrativos.css';

const UsuariosAdministrativos = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    department: '',
    avatar_url: '',
    is_active: true
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  // Departamentos disponíveis
  const departamentos = [
    'Administrativo',
    'Coordenação Pedagógica',
    'Tecnologia da Informação',
    'Recursos Humanos',
    'Financeiro',
    'Secretaria',
    'Direção'
  ];

  // Carregar dados iniciais
  useEffect(() => {
    loadData();
  }, []);

  // Buscar com filtro
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      handleSearch();
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersResult, statsResult] = await Promise.all([
        getAllAdminUsers(),
        getAdminUsersStats()
      ]);

      if (usersResult.success) {
        setUsuarios(usersResult.data);
      }

      if (statsResult.success) {
        setStats(statsResult.data);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      const result = await searchAdminUsers(searchTerm);
      if (result.success) {
        setUsuarios(result.data);
      }
    } catch (error) {
      console.error('Erro na busca:', error);
    }
  };

  const openModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '', // Não carregar senha existente
        phone: user.phone || '',
        department: user.department || '',
        avatar_url: user.avatar_url || '',
        is_active: user.is_active
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        phone: '',
        department: '',
        avatar_url: '',
        is_active: true
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      phone: '',
      department: '',
      avatar_url: '',
      is_active: true
    });
    setFormErrors({});
    setShowPassword(false);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Nome é obrigatório';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email inválido';
    }

    // Senha obrigatória apenas para novos usuários
    if (!editingUser && !formData.password.trim()) {
      errors.password = 'Senha é obrigatória para novos usuários';
    } else if (formData.password && formData.password.length < 6) {
      errors.password = 'Senha deve ter pelo menos 6 caracteres';
    }

    if (!formData.department.trim()) {
      errors.department = 'Departamento é obrigatório';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitLoading(true);

    try {
      let result;
      
      if (editingUser) {
        // Atualizar usuário existente
        result = await updateAdminUser(editingUser.id, formData);
      } else {
        // Criar novo usuário
        result = await createAdminUser(formData);
      }

      if (result.success) {
        closeModal();
        loadData(); // Recarregar dados
        alert(editingUser ? 'Usuário atualizado com sucesso!' : 'Usuário criado com sucesso!');
      } else {
        setFormErrors({ submit: result.error });
      }
    } catch (error) {
      setFormErrors({ submit: 'Erro interno. Tente novamente.' });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      const result = await toggleAdminUserStatus(user.id, !user.is_active);
      if (result.success) {
        loadData(); // Recarregar dados
        alert(`Usuário ${!user.is_active ? 'ativado' : 'desativado'} com sucesso!`);
      } else {
        alert('Erro ao alterar status: ' + result.error);
      }
    } catch (error) {
      alert('Erro interno ao alterar status');
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Tem certeza que deseja excluir o usuário "${user.name}"?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      const result = await deleteAdminUser(user.id);
      if (result.success) {
        loadData(); // Recarregar dados
        alert('Usuário excluído com sucesso!');
      } else {
        alert('Erro ao excluir usuário: ' + result.error);
      }
    } catch (error) {
      alert('Erro interno ao excluir usuário');
    }
  };

  if (loading) {
    return (
      <div className="usuarios-administrativos">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando usuários administrativos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="usuarios-administrativos">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h1 className="page-title">Usuários Administrativos</h1>
            <p className="page-subtitle">Gerenciamento de usuários com acesso administrativo</p>
          </div>
          <button className="btn-primary" onClick={() => openModal()}>
            <Plus size={20} />
            Novo Usuário
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon">
              <Users size={24} />
            </div>
          </div>
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total de Administradores</div>
        </div>

        <div className="stat-card success">
          <div className="stat-header">
            <div className="stat-icon success">
              <UserCheck size={24} />
            </div>
          </div>
          <div className="stat-value">{stats.active}</div>
          <div className="stat-label">Usuários Ativos</div>
        </div>

        <div className="stat-card warning">
          <div className="stat-header">
            <div className="stat-icon warning">
              <Shield size={24} />
            </div>
          </div>
          <div className="stat-value">{stats.inactive}</div>
          <div className="stat-label">Usuários Inativos</div>
        </div>

        <div className="stat-card info">
          <div className="stat-header">
            <div className="stat-icon info">
              <Settings size={24} />
            </div>
          </div>
          <div className="stat-value">{departamentos.length}</div>
          <div className="stat-label">Departamentos</div>
        </div>
      </div>

      <div className="content-card">
        <div className="table-header">
          <div className="search-container">
            <Search size={20} />
            <input
              type="text"
              placeholder="Buscar por nome ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Email</th>
                <th>Departamento</th>
                <th>Telefone</th>
                <th>Status</th>
                <th>Criado em</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">
                    {searchTerm ? 'Nenhum usuário encontrado com os filtros aplicados' : 'Nenhum usuário administrativo cadastrado'}
                  </td>
                </tr>
              ) : (
                usuarios.map(usuario => (
                  <tr key={usuario.id}>
                    <td>
                      <div className="user-info">
                        {usuario.avatar_url ? (
                          <img src={usuario.avatar_url} alt={usuario.name} className="user-avatar" />
                        ) : (
                          <div className="user-avatar-placeholder">
                            {usuario.name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                        )}
                        <span>{usuario.name}</span>
                      </div>
                    </td>
                    <td>{usuario.email}</td>
                    <td>{usuario.department}</td>
                    <td>{usuario.phone || '-'}</td>
                    <td>
                      <button
                        className={`status-toggle ${usuario.is_active ? 'active' : 'inactive'}`}
                        onClick={() => handleToggleStatus(usuario)}
                        title={`Clique para ${usuario.is_active ? 'desativar' : 'ativar'}`}
                      >
                        {usuario.is_active ? 'Ativo' : 'Inativo'}
                      </button>
                    </td>
                    <td>{new Date(usuario.created_at).toLocaleDateString('pt-BR')}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-action edit"
                          onClick={() => openModal(usuario)}
                          title="Editar usuário"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          className="btn-action delete"
                          onClick={() => handleDelete(usuario)}
                          title="Excluir usuário"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Formulário */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Editar Usuário' : 'Novo Usuário Administrativo'}</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="name">Nome Completo *</label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className={formErrors.name ? 'error' : ''}
                    placeholder="Digite o nome completo"
                  />
                  {formErrors.name && <span className="error-message">{formErrors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className={formErrors.email ? 'error' : ''}
                    placeholder="Digite o email"
                    disabled={editingUser} // Não permitir alterar email
                  />
                  {formErrors.email && <span className="error-message">{formErrors.email}</span>}
                </div>

                {!editingUser && (
                  <div className="form-group">
                    <label htmlFor="password">Senha *</label>
                    <div className="password-input">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        className={formErrors.password ? 'error' : ''}
                        placeholder="Digite a senha"
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {formErrors.password && <span className="error-message">{formErrors.password}</span>}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="phone">Telefone</label>
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="(11) 99999-9999"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="department">Departamento *</label>
                  <select
                    id="department"
                    value={formData.department}
                    onChange={(e) => setFormData({...formData, department: e.target.value})}
                    className={formErrors.department ? 'error' : ''}
                  >
                    <option value="">Selecione um departamento</option>
                    {departamentos.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                  {formErrors.department && <span className="error-message">{formErrors.department}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="avatar_url">URL do Avatar</label>
                  <input
                    type="url"
                    id="avatar_url"
                    value={formData.avatar_url}
                    onChange={(e) => setFormData({...formData, avatar_url: e.target.value})}
                    placeholder="https://exemplo.com/avatar.jpg"
                  />
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  />
                  <span className="checkmark"></span>
                  Usuário ativo
                </label>
              </div>

              {formErrors.submit && (
                <div className="error-message submit-error">{formErrors.submit}</div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={submitLoading}>
                  {submitLoading ? 'Salvando...' : (editingUser ? 'Atualizar' : 'Criar Usuário')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsuariosAdministrativos;
